import { EditOutlined } from '@ant-design/icons';
import type { Form } from 'antd';
import type { Rule } from 'antd/es/form';
import React, { useCallback, useState } from 'react';

import { EmojiModal } from 'app/components/commons/Package/Headlines/EmojiModal/EmojiModal';
import type { Picture } from 'app/typings';

import type { Mode } from '../Hotel/Types';
import { NoPicture } from '../NoPicture/NoPicture';
import { PictureComponent } from '../PictureComponent/PictureComponent';

import './EmojiSelector.scss';

type EmojiSelectorProps = {
  mode: Mode;
  value?: Picture;
  onChange?: (emoji: Picture) => void;
  rules?: Rule[];
  formItemProps?: React.ComponentProps<typeof Form.Item>;
  disabled?: boolean;
};

export const EmojiSelector: React.FC<EmojiSelectorProps> = ({
  mode,
  value,
  onChange,
  rules = [{ required: true, message: 'Required' }],
  formItemProps,
  disabled = false,
}) => {
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isHover, setIsOver] = useState(false);

  const handleSelect = useCallback(
    (emoji: Picture) => {
      if (onChange) {
        onChange(emoji);
      }

      setIsEmojiOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mode === 'edit' && (e.key === 'Enter' || e.key === ' ')) {
      setIsEmojiOpen(true);
    }
  };

  return (
    <div className="input-row">
      <div className="experience__avatar-container">
        <div
          role={mode === 'edit' ? 'button' : undefined}
          tabIndex={mode === 'edit' ? 0 : -1}
          aria-label="Choisir un emoji"
          onClick={mode === 'edit' ? () => setIsEmojiOpen(true) : undefined}
          onKeyDown={handleKeyDown}
          className={`experience__avatar${
            mode === 'edit' ? ' experience__avatar-clickable' : ''
          }`}
          key={value?.pictureId}
          style={{
            cursor: mode === 'edit' ? 'pointer' : 'default',
            outline: 'none',
          }}
        >
          {value ? <PictureComponent {...value} width={104} /> : <NoPicture />}
        </div>
        {mode === 'edit' && (
          <div
            onMouseOut={() => setIsOver(false)}
            onMouseOver={() => setIsOver(true)}
            className="experience__avatar__hover-area"
          >
            {isHover && (
              <div className="upload-item__hover-buttons">
                <EditOutlined onClick={() => setIsEmojiOpen(true)} />
              </div>
            )}
          </div>
        )}
      </div>
      <EmojiModal
        visible={isEmojiOpen}
        onSelect={handleSelect}
        onCancel={() => setIsEmojiOpen(false)}
      />
    </div>
  );
};
